import React from "react"
import * as prismicH from "@prismicio/helpers"
import { Typography } from "antd"
import Image from "next/legacy/image"
import Link from "next/link"
import { Elements, HTMLSerializer, LinkResolver } from "prismic-reactjs"
import reactStringReplace from "react-string-replace"
import { css } from "@emotion/react"
import { md, sm, xs } from "../../styles/mediaQuery"
import { nanoid } from "nanoid"
import styled from "@emotion/styled"
import { StyledParagraph } from "../../components/StyledParagraph/StyledParagraph"

export const linkResolver: LinkResolver = (doc) => {
  if (doc.type === "blog") return "/blog/" + doc.uid
  if (doc.type === "page") return "/" + doc.uid
  return "/" + doc.id
}

const serializeTextWithSpans = (text: string, spans: any[]): React.ReactNode[] => {
  if (!spans || spans.length === 0) return [text]

  const result: React.ReactNode[] = []
  let cursor = 0

  spans.forEach((span, i) => {
    // Přidat text před spanem
    if (span.start > cursor) {
      result.push(text.slice(cursor, span.start))
    }

    const spanText = text.slice(span.start, span.end)
    let wrapped: React.ReactNode = spanText

    switch (span.type) {
      case "strong":
        wrapped = <strong key={`strong-${i}`}>{spanText}</strong>
        break
      case "em":
        wrapped = <em key={`em-${i}`}>{spanText}</em>
        break
      case "hyperlink": {
        const href = span.data?.url || prismicH.asLink(span.data, linkResolver)
        if (href) {
          wrapped = (
            <Link key={`link-${i}`} href={href} target={span.data?.target}>
              {spanText}
            </Link>
          )
        }
        break
      }
    }

    result.push(wrapped)
    cursor = span.end
  })

  // Zbytek textu po posledním spanu
  if (cursor < text.length) {
    result.push(text.slice(cursor))
  }

  return result
}

const renderListItem = (element: any): React.ReactNode => {
  const key = nanoid()
  const serialized = serializeTextWithSpans(element.text, element.spans)
  const { replacedText, wasUrlFound } = replaceUrlsToLink(serialized)

  return <li key={key}>{wasUrlFound ? replacedText : serialized}</li>
}

const replaceUrlsToLink = (input: React.ReactNode | React.ReactNode[]) => {
  let wasUrlFound = false

  const nodes = React.Children.toArray(input).flatMap((child, index) => {
    if (typeof child === "string") {
      return reactStringReplace(child, /(https?:\/\/\S+)/g, (match, i) => {
        wasUrlFound = true

        if (match.includes("vzdelanibudoucnosti") || match.includes("localhost")) {
          return (
            <Link key={`${match}-${index}-${i}`} target="_blank" href={match} legacyBehavior>
              {match}
            </Link>
          )
        }

        return (
          <Typography.Link key={`${match}-${index}-${i}`} target="_blank" href={match}>
            {match}
          </Typography.Link>
        )
      })
    }

    return child
  })

  return { replacedText: nodes, wasUrlFound }
}

export const htmlSerializer: HTMLSerializer<React.ReactNode> = (type, element, content, children): React.ReactNode => {
  const key = nanoid()

  switch (type) {
    case Elements.heading1:
      return (
        <Typography.Title key={key} level={1}>
          {element.text}
        </Typography.Title>
      )
    case Elements.heading2:
      return (
        <Typography.Title key={key} level={2}>
          {element.text}
        </Typography.Title>
      )
    case Elements.heading3:
      return (
        <Typography.Title key={key} level={3}>
          {element.text}
        </Typography.Title>
      )
    case Elements.heading4:
      return (
        <Typography.Title key={key} level={4}>
          {element.text}
        </Typography.Title>
      )
    case Elements.heading5:
      return (
        <Typography.Title key={key} level={5}>
          {element.text}
        </Typography.Title>
      )
    case Elements.heading6:
      return (
        <Typography.Title key={key} level={5}>
          {element.text}
        </Typography.Title>
      )
    case Elements.paragraph: {
      const { replacedText, wasUrlFound } = replaceUrlsToLink(children)
      return <StyledParagraph key={key}>{wasUrlFound ? replacedText : children}</StyledParagraph>
    }
    case Elements.preformatted:
      return <pre key={key}>{children.join("")}</pre>
    case Elements.strong:
      return (
        <Typography.Text key={key} strong>
          {children}
        </Typography.Text>
      )
    case Elements.em:
      return <em key={key}>{children}</em>
    case Elements.listItem:
      return renderListItem(element)

    case Elements.oListItem:
      return renderListItem(element)
    case Elements.list:
      return <ul key={key}>{children}</ul>
    case Elements.oList:
      return <ol key={key}>{children}</ol>

    case Elements.image:
      const imageLinkUrl = element.linkTo ? prismicH.asLink(element.linkTo, linkResolver) : null
      const imageComponent = (
        <div
          key={key}
          css={css`
            float: right;
            width: 50%;
            min-width: 200px;
            height: auto;
            padding: 0 0 20px 20px;

            ${xs} {
              display: none;
            }
          `}>
          <div css={imageEmbedStyleWrapper()}>
            <Image alt={element.alt || "image-alt"} src={element.url} width={element.dimensions.width} height={element.dimensions.height} layout="responsive" />
            {element.alt && (
              <Typography.Text type="secondary">
                <em>{element.alt}</em>
              </Typography.Text>
            )}
          </div>
        </div>
      )

      if (imageLinkUrl) {
        return (
          <Link key={key} href={imageLinkUrl} target={element.linkTo.target}>
            {imageComponent}
          </Link>
        )
      }

      return imageComponent

    case Elements.embed:
      const youtubeEmbeddedUrl =
        element?.oembed?.provider_name?.toLowerCase() === "youtube"
          ? `//www.youtube.com/embed/${getYoutubeId(element.oembed.embed_url)}`
          : element.oembed.embed_url

      return (
        <EmbeddedVideoWrapper key={key}>
          <div data-oembed={element.oembed.embed_url} data-oembed-type={element.oembed.type} data-oembed-provider={element.oembed.provider_name}>
            <iframe
              width={element.oembed.width}
              height={element.oembed.height}
              src={youtubeEmbeddedUrl}
              frameBorder={0}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </EmbeddedVideoWrapper>
      )

    case Elements.hyperlink:
      if (element.data?.link_type?.toLowerCase() === "web") {
        if (!element.data.url) return null
        return (
          <Link key={key} href={element.data.url} target={element.data.target}>
            {children}
          </Link>
        )
      }

      const linkUrl = prismicH.asLink(element.data, linkResolver)
      if (!linkUrl) return null

      return (
        <Link key={key} href={linkUrl} target={element.data.target}>
          {children}
        </Link>
      )

    case Elements.label:
      return <span key={key}>{children}</span>

    case Elements.span:
      return content ? reactStringReplace(content, /(?:\r\n|\r|\n)/g, () => <br />) : null

    default:
      return null
  }
}

const imageEmbedStyleWrapper = () => css`
  margin: 0 auto;
  position: relative;

  ${md} {
    width: 100%;
    height: auto;
    min-height: 300px;
  }

  ${sm} {
    width: 100%;
    height: auto;
    min-height: 30vh;
  }
`

const EmbeddedVideoWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 0;

  ${md} {
    width: 100%;
    height: auto;
  }

  > div {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
`

const getYoutubeId = (url: string) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}
